import { ComponentProps, FC, ReactNode } from "react";
import { Chip, makeStyles } from "@material-ui/core";

import theme from "theme";

export type Color = "green" | "red" | "yellow" | "purple";

const colors: { [key in Color]: string } = {
  green: theme.custom.palette.green.main,
  red: theme.custom.palette.red.main,
  yellow: theme.custom.palette.yellow.main,
  purple: theme.custom.palette.purple.main,
};

interface StyleProps {
  color?: Color;
  filled?: boolean;
  fitContent?: boolean;
  small?: boolean;
  clickable?: boolean;
}

const useStyles = makeStyles(theme => ({
  tag: (props: StyleProps) => ({
    backgroundColor: props.filled ? colors[props.color || "purple"] : "white",
    color: props.filled
      ? "#FFFFFF"
      : props.color
      ? colors[props.color]
      : "#828282",
    border: props.small
      ? "none"
      : `1px solid ${props.color ? colors[props.color] : "#e1e3e3"}`,
    borderRadius: props.small ? 4 : theme.spacing(2),
    padding: props.small ? "4px" : "7px 15px",
    fontSize: props.small ? "90%" : 12,
    fontWeight:
      props.filled || props.small
        ? theme.typography.fontWeightMedium
        : "normal",
    whiteSpace: "nowrap",
    width: props.fitContent || props.small ? "fit-content" : "auto",
    display: props.small ? "inline" : "inline-flex",
    height: "auto",
    cursor: props.clickable ? "pointer" : "default",
    "& .MuiChip-label": {
      padding: 0,
    },
    "&:hover": props.clickable
      ? {
          backgroundColor: props.filled
            ? colors[props.color || "purple"]
            : "white",
        }
      : {},
    "&:focus": {
      backgroundColor: props.filled ? colors[props.color || "purple"] : "white",
    },
  }),
}));

interface TagProps extends Omit<ComponentProps<"div">, "ref"> {
  color?: Color;
  filled?: boolean;
  fitContent?: boolean;
  small?: boolean;
  children: ReactNode;
}

const Tag: FC<TagProps> = ({
  color,
  filled = false,
  fitContent,
  small,
  onClick,
  children,
  ...props
}) => {
  const styleProps: StyleProps = {
    color,
    filled,
    fitContent,
    small,
    clickable: !!onClick,
  };
  const classes = useStyles(styleProps);

  return (
    <Chip
      label={children}
      className={classes.tag}
      onClick={onClick}
      {...props}
    />
  );
};

export default Tag;
